import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSeo } from "../hooks/useSeo";
import { Reveal } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import { SERVICES, FAQ } from "../data/content";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "LegalService",
  name: "The Law Office of Ronald K. Nolen, LLC",
  description: "Strategic legal services for business, property, creative work, and personal matters in Chicago.",
  areaServed: "Chicago, Illinois",
  url: "https://www.rknolenlaw.org/services",
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Legal Services",
    itemListElement: SERVICES.map((s) => ({ "@type": "Offer", name: s.title })),
  },
};

export default function Services() {
  useSeo({
    title: "Legal Services & Practice Areas | Nolen Law Chicago",
    description:
      "Strategic Chicago legal services: entertainment, sports, contracts, real estate, trademark, copyright, outside general counsel, litigation, family law, and criminal defense.",
    path: "/services",
    schema: SCHEMA,
  });

  return (
    <>
      {/* Hero (no photo — CSS architecture) */}
      <section className="relative overflow-hidden bg-warmwhite pt-36 pb-16 md:pt-44 md:pb-24" data-testid="services-hero">
        <div className="arch-grid pointer-events-none absolute inset-0 opacity-40" />
        <div className="river-lines pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-8">
          <Reveal>
            <p className="text-xs font-600 uppercase tracking-label text-gold">Practice Areas</p>
            <h1 className="mt-4 max-w-4xl font-serif text-4xl font-500 leading-[1.03] text-navy sm:text-6xl">
              Strategic Legal Services for Business, Property, Creative Work &amp; Personal Matters
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-bluegray-text">
              Practical legal counsel designed to protect your interests, reduce exposure, and help you
              move forward with greater confidence.
            </p>
            <div className="mt-8 h-px w-24 bg-gold" />
          </Reveal>
          {/* Quick anchor index */}
          <Reveal delay={0.1}>
            <nav className="mt-10 flex flex-wrap gap-2" aria-label="Practice area index">
              {SERVICES.map((s) => (
                <a key={s.anchor} href={`#${s.anchor}`} data-testid={`services-index-${s.anchor}`}
                  className="rounded-sm border border-navy/15 bg-white px-4 py-2 text-xs font-600 text-navy transition-colors hover:border-gold hover:text-gold">
                  {s.title}
                </a>
              ))}
            </nav>
          </Reveal>
        </div>
      </section>

      {/* Service sections */}
      <div>
        {SERVICES.map((s, idx) => (
          <section
            key={s.anchor}
            id={s.anchor}
            data-testid={`service-section-${s.anchor}`}
            className={idx % 2 === 0 ? "bg-warmwhite py-16 md:py-20" : "bg-cream py-16 md:py-20"}
          >
            <div className="mx-auto grid max-w-7xl gap-8 px-5 md:px-8 lg:grid-cols-[0.9fr_1.4fr] lg:gap-16">
              <Reveal>
                <span className="font-serif text-5xl font-500 text-gold">{s.n}</span>
                <h2 className="mt-3 font-serif text-3xl font-500 leading-tight text-navy sm:text-4xl">{s.title}</h2>
                <Link to="/contact" data-testid={`service-cta-${s.anchor}`}
                  className="group mt-6 inline-flex items-center gap-2 rounded-sm border border-navy/20 px-6 py-3 text-sm font-600 text-navy transition-colors hover:border-gold hover:bg-navy hover:text-white">
                  Discuss Your Matter
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.8} />
                </Link>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="text-lg leading-relaxed text-bluegray-text">{s.copy}</p>
                {s.items.length > 0 && (
                  <ul className="mt-7 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                    {s.items.map((it) => (
                      <li key={it} className="flex items-start gap-3 text-sm leading-relaxed text-navy">
                        <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rotate-45 bg-gold" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Reveal>
            </div>
          </section>
        ))}
      </div>

      {/* FAQ */}
      <section className="bg-warmwhite py-20 md:py-28" data-testid="services-faq">
        <div className="mx-auto max-w-4xl px-5 md:px-8">
          <Reveal>
            <p className="text-xs font-600 uppercase tracking-label text-gold">Common Questions</p>
            <h2 className="mt-4 font-serif text-4xl font-500 leading-[1.05] text-navy sm:text-5xl">
              Frequently Asked Questions
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <Accordion type="single" collapsible className="w-full">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-navy/10"
                  data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="py-6 text-left font-serif text-xl font-500 text-navy hover:text-gold hover:no-underline sm:text-2xl">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-base leading-relaxed text-bluegray-text">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <CTASection testid="services-cta" />
    </>
  );
}
